// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.
// Unauthorized use or distribution is prohibited.

import { Link } from 'react-router-dom'
import { GuardRokIcon } from '../components/GuardRokLogo'
import { CheckCircle, ArrowRight, Users, Zap, MessageSquare } from 'lucide-react'

const VERTICALS = [
  {
    industry: 'Healthcare',
    reg: 'HIPAA · HITECH · PHI',
    color: '#3b82f6',
    scenario: 'Clinical staff pasting patient records into AI tools before documentation workflows are in place.',
    spots: 1,
  },
  {
    industry: 'Legal',
    reg: 'Attorney-Client Privilege · ABA Rules',
    color: '#a78bfa',
    scenario: 'Associates using Claude or ChatGPT to draft memos that include privileged strategy and client identifiers.',
    spots: 1,
  },
  {
    industry: 'Financial Services',
    reg: 'FINRA · SOX · MNPI',
    color: '#22c55e',
    scenario: 'Analysts using Copilot to draft client memos that pull in non-public trading positions or earnings data.',
    spots: 1,
  },
  {
    industry: 'Real Estate',
    reg: 'RESPA · Fair Housing · NDA Obligations',
    color: '#f59e0b',
    scenario: 'Agents and brokers pasting client financial profiles, offer strategies, and deal terms into AI drafting tools.',
    spots: 1,
  },
]

const WHAT_YOU_GET = [
  {
    icon: Zap,
    title: 'Founding pricing locked permanently',
    body: 'Your team pays the founding rate for as long as you stay a customer — even after we exit beta and raise prices for new customers.',
  },
  {
    icon: MessageSquare,
    title: 'Direct line to the product team',
    body: 'Shared Slack channel with the GuardRok™ founders. Your edge cases, false positives, and feature requests go directly into the build queue — not a support ticket.',
  },
  {
    icon: Users,
    title: 'Shape what gets built',
    body: 'Design partners have shaped every major feature in the current product. Your industry\'s detection gaps, compliance requirements, and workflow needs define the next 90 days of roadmap.',
  },
]

const WHAT_WE_GET = [
  'A real case study with measurable compliance outcomes',
  'Permission to reference your industry vertical (not necessarily your name) in sales conversations',
  'Honest feedback on what doesn\'t work — we need this more than praise',
  'An introduction to one peer in your network if the product delivers value',
]

export default function DesignPartners() {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#030712',
        color: '#fff',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      {/* Nav */}
      <nav
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 48px',
          borderBottom: '1px solid #0f172a',
          position: 'sticky',
          top: 0,
          background: 'rgba(3,9,18,0.95)',
          backdropFilter: 'blur(12px)',
          zIndex: 100,
        }}
      >
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' }}>
          <GuardRokIcon size={36} />
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, letterSpacing: '-0.5px', color: '#fff' }}>
              Guard<span style={{ color: '#E63946' }}>Rok</span>
              <span style={{ fontSize: 8, color: '#6b7280', verticalAlign: 'super', marginLeft: 1 }}>™</span>
            </div>
            <div style={{ fontSize: 9, letterSpacing: '3px', color: '#6b7280', textTransform: 'uppercase', marginTop: 2 }}>
              AI Firewall
            </div>
          </div>
        </Link>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Link to="/enterprise" style={{ color: '#9ca3af', fontSize: 14, textDecoration: 'none' }}>Enterprise</Link>
          <Link to="/security" style={{ color: '#9ca3af', fontSize: 14, textDecoration: 'none' }}>Security</Link>
          <a
            href="mailto:hello@guardrok.com?subject=Design Partner Application"
            style={{
              background: '#E63946',
              color: '#fff',
              fontSize: 13,
              fontWeight: 700,
              padding: '8px 18px',
              borderRadius: 8,
              textDecoration: 'none',
            }}
          >
            Apply now
          </a>
        </div>
      </nav>

      {/* Hero */}
      <div style={{ maxWidth: 820, margin: '0 auto', padding: '72px 40px 56px', textAlign: 'center' }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: '#0f172a',
            border: '1px solid rgba(230,57,70,0.25)',
            borderRadius: 20,
            padding: '5px 14px',
            marginBottom: 24,
          }}
        >
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 6px #22c55e' }} />
          <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>{VERTICALS.filter(v => v.spots > 0).length} spots available</span>
        </div>
        <h1
          style={{
            fontSize: 50,
            fontWeight: 900,
            letterSpacing: '-2px',
            lineHeight: 1.05,
            marginBottom: 18,
          }}
        >
          Design Partner Program
          <br />
          <span style={{ color: '#E63946' }}>Healthcare · Legal · Finance · Real Estate</span>
        </h1>
        <p style={{ fontSize: 17, color: '#94a3b8', maxWidth: 600, margin: '0 auto 36px', lineHeight: 1.65 }}>
          We're looking for four organisations — one per vertical — who are willing to build
          GuardRok™ with us. You get the product shaped around your industry's needs.
          We get a real case study from a real regulated organisation.
        </p>
        <a
          href="mailto:hello@guardrok.com?subject=Design Partner Application"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: '#E63946',
            color: '#fff',
            fontWeight: 700,
            fontSize: 15,
            padding: '14px 28px',
            borderRadius: 10,
            textDecoration: 'none',
          }}
        >
          Apply — hello@guardrok.com <ArrowRight size={16} />
        </a>
        <p style={{ fontSize: 12, color: '#374151', marginTop: 14 }}>
          No forms. Email hello@guardrok.com with your industry, team size, and the AI tools your team uses.
          We'll respond within 24 hours.
        </p>
      </div>

      {/* Open spots */}
      <div style={{ maxWidth: 980, margin: '0 auto', padding: '0 40px 64px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#E63946', letterSpacing: '2px', textTransform: 'uppercase', textAlign: 'center', marginBottom: 10 }}>
          Open spots
        </div>
        <h2 style={{ fontSize: 24, fontWeight: 800, textAlign: 'center', marginBottom: 36, letterSpacing: '-0.5px' }}>
          One organisation per vertical
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20 }}>
          {VERTICALS.map(({ industry, reg, color, scenario, spots }) => (
            <div
              key={industry}
              style={{
                background: '#0d1117',
                border: '1px solid #1e293b',
                borderRadius: 14,
                padding: '28px',
                position: 'relative',
              }}
            >
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: 'rgba(34,197,94,0.1)',
                  border: '1px solid rgba(34,197,94,0.25)',
                  borderRadius: 20,
                  padding: '3px 10px',
                  marginBottom: 16,
                }}
              >
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#22c55e' }} />
                <span style={{ fontSize: 10, color: '#22c55e', fontWeight: 700 }}>{spots} spot open</span>
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#f1f5f9', marginBottom: 6 }}>{industry}</div>
              <div style={{ fontSize: 11, color, fontWeight: 600, marginBottom: 14, letterSpacing: '0.5px' }}>{reg}</div>
              <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.65, margin: 0 }}>
                <strong style={{ color: '#94a3b8' }}>The scenario we're solving:</strong> {scenario}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* What you get */}
      <div style={{ background: '#08111e', borderTop: '1px solid #0f172a', borderBottom: '1px solid #0f172a', padding: '64px 40px' }}>
        <div style={{ maxWidth: 980, margin: '0 auto' }}>
          <h2 style={{ fontSize: 24, fontWeight: 800, textAlign: 'center', marginBottom: 36, letterSpacing: '-0.5px' }}>
            What design partners get
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20 }}>
            {WHAT_YOU_GET.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                style={{ background: '#0d1117', border: '1px solid #1e293b', borderRadius: 14, padding: '24px' }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: 'rgba(230,57,70,0.1)',
                    border: '1px solid rgba(230,57,70,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 14,
                  }}
                >
                  <Icon size={16} color="#E63946" />
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9', marginBottom: 8 }}>{title}</div>
                <p style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.65, margin: 0 }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* What we ask for */}
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '64px 40px' }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8, letterSpacing: '-0.5px' }}>
          What we ask in return
        </h2>
        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 28, lineHeight: 1.6 }}>
          This is a genuine exchange. We're not looking for logos to put on a slide — we need four organisations
          willing to tell us what's broken, what's missing, and what would actually get this deployed at scale.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {WHAT_WE_GET.map(item => (
            <div key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <CheckCircle size={15} color="#22c55e" style={{ flexShrink: 0, marginTop: 2 }} />
              <span style={{ fontSize: 14, color: '#cbd5e1', lineHeight: 1.6 }}>{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Honest section — what we are and aren't */}
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '0 40px 64px' }}>
        <div
          style={{
            background: '#0d1117',
            border: '1px solid #1e293b',
            borderRadius: 14,
            padding: '32px',
          }}
        >
          <h3 style={{ fontSize: 17, fontWeight: 800, marginBottom: 14 }}>The honest version</h3>
          <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.75, marginBottom: 12 }}>
            GuardRok™ is a live, working product — a shipping Chrome extension that blocks sensitive
            data across every major AI tool today. A real deployment, not a mockup or a pitch deck.
          </p>
          <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.75, marginBottom: 12 }}>
            We're early on purpose. Our SOC 2 Type I audit is in progress, and design partners are
            how we turn a strong product into documented, reference-able compliance outcomes. Coming
            in now is what locks your founding rate and puts your industry's needs at the front of
            the roadmap.
          </p>
          <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.75 }}>
            Straight talk on fit: if your procurement requires a completed SOC 2 report before any
            pilot, we're likely a few months early — and we'll tell you that on the first call
            rather than waste your time. If you can pilot under a mutual NDA while the audit
            finishes, let's talk.
          </p>
        </div>
      </div>

      {/* CTA */}
      <div style={{ maxWidth: 640, margin: '0 auto 80px', padding: '0 40px', textAlign: 'center' }}>
        <div
          style={{
            background: '#0d1117',
            border: '1px solid rgba(230,57,70,0.25)',
            borderRadius: 16,
            padding: '44px 40px',
          }}
        >
          <h2 style={{ fontSize: 26, fontWeight: 900, marginBottom: 10, letterSpacing: '-0.5px' }}>
            Apply for a design partner spot
          </h2>
          <p style={{ fontSize: 14, color: '#94a3b8', marginBottom: 28, lineHeight: 1.65 }}>
            Email hello@guardrok.com with: your industry, team size, which AI tools your team
            currently uses, and the compliance concern you're trying to address.
            We'll respond within 24 hours.
          </p>
          <a
            href="mailto:hello@guardrok.com?subject=Design Partner Application - [Your Industry]&body=Industry: %0ATeam size: %0AAI tools currently used: %0ACompliance concern: "
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: '#E63946',
              color: '#fff',
              fontWeight: 700,
              fontSize: 14,
              padding: '13px 26px',
              borderRadius: 10,
              textDecoration: 'none',
            }}
          >
            Email us now <ArrowRight size={15} />
          </a>
          <p style={{ fontSize: 11, color: '#374151', marginTop: 16 }}>
            hello@guardrok.com · 4 spots · 1 per vertical · founding pricing locked permanently
          </p>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          borderTop: '1px solid #0f172a',
          padding: '24px 48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div style={{ fontSize: 13, color: '#374151' }}>© 2026 GuardRok™ · aiApas LLC</div>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <Link to="/enterprise" style={{ fontSize: 13, color: '#4b5563', textDecoration: 'none' }}>Enterprise</Link>
          <Link to="/security" style={{ fontSize: 13, color: '#4b5563', textDecoration: 'none' }}>Security</Link>
          <Link to="/pricing" style={{ fontSize: 13, color: '#4b5563', textDecoration: 'none' }}>Pricing</Link>
          <Link to="/privacy" style={{ fontSize: 13, color: '#4b5563', textDecoration: 'none' }}>Privacy</Link>
          <a href="mailto:hello@guardrok.com" style={{ fontSize: 13, color: '#4b5563', textDecoration: 'none' }}>Contact</a>
        </div>
      </div>
    </div>
  )
}
